import { FC, Suspense, useEffect, useState } from 'react'
import { Box, useMediaQuery, SimpleGrid } from '@chakra-ui/react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AppRoute, routes } from '../pages/routes'
import { useAppDispatch, useAppSelector } from './typedReduxHooks'
import { DESKTOP_BREAKPOINT } from '../constants'
import HeaderBar from '../components/shared-components/HeaderBar'
import Nav from '../components/shared-components/SideNav'
import IdleTimer from '../utils/idle-timer'
import UserAgreementPage from '../pages/UserAgreementPage'
import { appDidFinishLaunching } from './appSlice'
import { useLogout } from '../hooks/useLogout'
import './styles/App.css'
import { ssoClientConfigMappings } from '../features/sso/constant'
import { Permission } from '../types/permission'
import MaintenanceBanner from '../components/MaintenanceBanner'
import { InitLocationModal } from '../features/locations/components/InitLocationModal'
import { useHasEntitlement } from '../pages/OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../types/organization-entitlements'

const App = () => {
  const dispatch = useAppDispatch()
  const idleTimeout = useAppSelector((state) => state.organization?.data?.settings?.idleTimeout)
  const logout = useLogout()

  // Runs once on mount — handles page refresh initialization only
  useEffect(() => {
    if (!localStorage.getItem('access_token')) return
    dispatch(appDidFinishLaunching())
  }, [])

  // Runs when idleTimeout changes — manages the idle timer only
  useEffect(() => {
    const timer = new IdleTimer({
      onTimeout: () => {
        if (
          !localStorage.getItem('access_token') &&
          !localStorage.getItem('refresh_token') &&
          !localStorage.getItem('client_id')
        ) {
          return
        }
        logout()
      },
      timeoutInMs:
        (idleTimeout && (idleTimeout <= 5400 || idleTimeout >= 300) ? idleTimeout : 900) * 1000, // default value: 15 mins
    })
    return () => timer.cleanUpTracker()
  }, [idleTimeout, logout])

  return (
    <>
      <MaintenanceBanner />
      <Suspense fallback={null}>
        <Routes>
          {Object.values(routes).map(({ path, Component, requiresAuth }) => (
            <Route
              path={path}
              element={
                requiresAuth ? <AuthComponent path={path} Component={Component} /> : <Component />
              }
              key={path}
            />
          ))}
        </Routes>
      </Suspense>
    </>
  )
}
const routesWithoutNav = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.selectRoles.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.pricePlan.path,
  routes.error.path,
  routes.root.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
]

const routesWithoutUserProfile = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.selectRoles.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
]

const routesShouldRedirectAfterLogin = [
  routes.approveUserInvitation.path,
  routes.approveJoinRequest.path,
  routes.settings.path,
]

const routesShouldGoToMainPage = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.selectRoles.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.login.path,
  routes.root.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
]

const AuthComponent: FC<AppRoute> = ({ Component, path }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const logout = useLogout()

  const [isSmallScreenNavOpen, setIsSmallScreenNavOpen] = useState(false)
  const [isDesktop] = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT})`)
  const clientId = localStorage.getItem('client_id') || ''
  const ssoClient = ssoClientConfigMappings.find((client) => client.clientId === clientId)
  const authentication = useAppSelector((state) => state.authentication)
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const isClientOnlyCreatePrescriber = ssoClient?.prescriberOnly
  const isClientHasFixedOrganization = ssoClient?.orgId

  useEffect(() => {
    if (!authentication?.userId) {
      if (
        routesShouldRedirectAfterLogin.includes(location.pathname) &&
        location.search.includes('external')
      ) {
        const url = new URL(window.location.href)
        url.searchParams.delete('external')
        localStorage.setItem('redirectAfterLogin', url.pathname + url.search)
      }
      navigate(routes.login.path)
      return
    }
  }, [navigate, authentication])
  useEffect(() => {
    if (authentication?.hasError) {
      navigate(routes.error.path)
      return
    }
  }, [authentication, navigate])
  useEffect(() => {
    if (!authentication?.userId) return
    if (authentication?.hasError) return
    if (authentication?.loginFinished && !authentication?.hasUserProfile) {
      if (!routesWithoutUserProfile.includes(location.pathname)) {
        if (isClientHasFixedOrganization) {
          if (isClientOnlyCreatePrescriber) {
            navigate(routes.createPrescriberProfile.path)
          } else {
            navigate(routes.selectRoles.path)
          }
        } else {
          navigate(routes.joinOrganizationPending.path)
        }
      }
      return
    }
  }, [location.pathname, navigate, authentication, ssoClient])

  useEffect(() => {
    if (!authentication?.userId || authentication?.hasError) return
    const checkAuthentication = async () => {
      if (
        !localStorage.getItem('access_token') ||
        !localStorage.getItem('refresh_token') ||
        !localStorage.getItem('client_id')
      ) {
        await logout()
        return
      }
      if (authentication.hasUserProfile && routesShouldGoToMainPage.includes(location.pathname)) {
        if (authentication?.permissions?.includes(Permission.PRESCRIBING)) {
          navigate(routes.newScript.path)
          return
        } else {
          navigate(routes.settings.path)
          return
        }
      }
    }
    checkAuthentication()
  }, [authentication, dispatch, location.pathname, logout, navigate])

  if (!authentication?.userId) {
    return null
  }

  if (routesWithoutNav.includes(path)) {
    return <Component />
  }
  return (
    <>
      <Box>
        {!isDesktop && (
          <HeaderBar onToggleNav={() => setIsSmallScreenNavOpen(!isSmallScreenNavOpen)} />
        )}
        <SimpleGrid
          height={isDesktop ? '100vh' : 'calc(100vh - 67px)'}
          alignItems="stretch"
          bgColor="blackAlpha.100"
          columns={2}
          gridTemplateColumns={isDesktop ? 'auto 1fr' : '1fr'}
        >
          <Nav
            isSmallScreenNavOpen={isSmallScreenNavOpen}
            onCloseSmallScreenNav={() => setIsSmallScreenNavOpen(false)}
          />
          <Component />
        </SimpleGrid>
      </Box>
      <UserAgreementPage />
      {hasMultipleLocations && <InitLocationModal />}
    </>
  )
}

export default App
